import {
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
  type LexicalUpdateJSON,
} from "lexical";
import { createElement } from "react";

import { MergeTagChip } from "./MergeTagChip";

import { mergeTagsService, type ResolvedFilters } from "@/services/mergeTags";

export type SerializedMergeTagNode = Spread<
  {
    tag: string;
    label: string;
    /** @deprecated use filters instead */
    defaultValue?: string;
    /** @deprecated use filters instead */
    capfirst?: boolean;
    filters?: ResolvedFilters;
  },
  SerializedLexicalNode
>;

export class MergeTagNode extends DecoratorNode<
  ReturnType<typeof createElement>
> {
  __tag: string;
  __label: string;
  __filters: ResolvedFilters;

  static getType(): string {
    return "merge-tag";
  }

  static clone(node: MergeTagNode): MergeTagNode {
    return new MergeTagNode(
      node.__tag,
      node.__label,
      node.__filters,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedMergeTagNode): MergeTagNode {
    const filters: ResolvedFilters = serializedNode.filters
      ? { ...serializedNode.filters }
      : {};

    if (serializedNode.defaultValue !== undefined && !("default" in filters)) {
      filters.default = serializedNode.defaultValue;
    }
    if (serializedNode.capfirst === true && filters.textCase === undefined) {
      filters.textCase = "capfirst";
    }

    return new MergeTagNode(
      serializedNode.tag,
      serializedNode.label,
      mergeTagsService.filters.normalize(filters),
    ).updateFromJSON(serializedNode);
  }

  constructor(
    tag: string,
    label: string,
    filters?: ResolvedFilters,
    key?: NodeKey,
  ) {
    super(key);
    this.__tag = tag;
    this.__label = label;
    this.__filters = filters ?? {};
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedMergeTagNode>,
  ): this {
    return super.updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedMergeTagNode {
    return {
      ...super.exportJSON(),
      tag: this.__tag,
      label: this.__label,
      filters: this.__filters,
    };
  }

  createDOM(): HTMLElement {
    // Plain inline span: `display: contents` gives a zero rect, which Lexical's
    // caret scroll-into-view reads as "caret at (0,0)" and scrolls to top.
    return document.createElement("span");
  }

  updateDOM(): false {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isIsolated(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  getTextContent(): string {
    return mergeTagsService.ifElseWrapperBuilder(this.__tag, this.__filters);
  }

  getTag(): string {
    return this.getLatest().__tag;
  }

  getLabel(): string {
    return this.getLatest().__label;
  }

  /**
   * Repoints the chip at a different tag — used by the product reindex pass,
   * where a block's index decides what its chips read.
   */
  setTag(tag: string, label: string): this {
    const writable = this.getWritable();
    writable.__tag = tag;
    writable.__label = label;
    return writable;
  }

  getFilters(): ResolvedFilters {
    return this.getLatest().__filters;
  }

  setFilters(filters: ResolvedFilters): this {
    const writable = this.getWritable();
    writable.__filters = filters;
    return writable;
  }

  decorate(): ReturnType<typeof createElement> {
    return createElement(MergeTagChip, {
      nodeKey: this.getKey(),
      identifier: this.__tag,
      label: this.__label,
      filters: this.__filters,
    });
  }
}

export function $createMergeTagNode(
  tag: string,
  label: string,
  filters?: ResolvedFilters,
): MergeTagNode {
  return new MergeTagNode(tag, label, filters);
}

export function $isMergeTagNode(
  node: LexicalNode | null | undefined,
): node is MergeTagNode {
  return node instanceof MergeTagNode;
}
